/**
 * Centralized error handling utility for API responses and network errors
 * Provides consistent error messages and logging across the application
 */
export class ErrorHandler {
  /**
   * Formats error messages for user display
   * @param error The error object or message
   * @returns A user-friendly error message
   */
  static formatErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      // Handle specific error types
      if (error.message.includes('timeout')) {
        return 'Request timed out. Please check your connection and try again.';
      }
      
      if (error.message.includes('Network Error')) {
        return 'Network error. Please check your internet connection.';
      }
      
      if (error.message.includes('404')) {
        return 'The requested data was not found.';
      }
      
      if (error.message.includes('500')) {
        return 'Server error. Please try again later.';
      }
      
      if (error.message.includes('403')) {
        return 'Access denied. You may not have permission to view this data.';
      }
      
      return error.message;
    }
    
    if (typeof error === 'string') {
      return error;
    }
    
    return 'An unexpected error occurred. Please try again.';
  }

  /**
   * Logs errors with context information
   * @param error The error to log
   * @param context Additional context about where the error occurred
   */
  static logError(error: unknown, context: string): void {
    const timestamp = new Date().toISOString();
    const errorMessage = this.formatErrorMessage(error);
    
    console.error(`[${timestamp}] ${context}:`, {
      message: errorMessage,
      originalError: error,
      stack: error instanceof Error ? error.stack : undefined,
    });
  }

  /**
   * Determines if an error is retryable
   * @param error The error to check
   * @returns True if the error suggests a retry might succeed
   */
  static isRetryableError(error: unknown): boolean {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      return (
        message.includes('timeout') ||
        message.includes('network error') ||
        message.includes('500') ||
        message.includes('502') ||
        message.includes('503') ||
        message.includes('504')
      );
    }
    return false;
  }
}

export default ErrorHandler;